import { Checkbox, Input, Modal, Select, Switch, Tabs, Tag } from 'antd'
import type { HookAPI } from 'antd/es/modal/useModal'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import Loader from '@/components/Atoms/Loader/Loader'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import {
  AcceptPartnershipRequest,
  DirectionalContextDto,
  PartnershipContextResponse,
  PartnershipModel,
  StoreShortModel,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { sortPartnershipContext } from '@/utils/search/stores/sortStores'

import cn from './PartnershipFormModal.module.scss'

const partnershipFormSchema = z.object({
  myB2bSegmentId: z
    .string({ required_error: 'Выберите B2B сегмент' })
    .min(1, 'Выберите B2B сегмент'),
})

interface Props {
  open: boolean
  mode: 'accept' | 'settings'
  partnership: PartnershipModel | null
  onClose: () => void
  onSuccess: (updated: PartnershipModel) => void
  modal: HookAPI
}

interface FormState {
  enableMySharing: boolean
  showOutgoingList: boolean
  localOutgoingExclusions: number[]
  showIncomingList: boolean
  localIncomingExclusions: number[]
  myEtaDays: number
  myIncomingEtaDays: number
  mySharedSegments: string[]
  myB2bSegmentId: string | null
}

const EMPTY_FORM: FormState = {
  enableMySharing: true,
  showOutgoingList: false,
  localOutgoingExclusions: [],
  showIncomingList: false,
  localIncomingExclusions: [],
  myEtaDays: 0,
  myIncomingEtaDays: 0,
  mySharedSegments: [],
  myB2bSegmentId: null,
}

function buildFormFromSettings(p: PartnershipModel): FormState {
  const s = p.mySettings
  return {
    enableMySharing: s.sharesData,
    showOutgoingList: s.outgoingExclusionCodes.length > 0,
    localOutgoingExclusions: [...s.outgoingExclusionCodes],
    showIncomingList: s.incomingExclusionCodes.length > 0,
    localIncomingExclusions: [...s.incomingExclusionCodes],
    myEtaDays: s.etaDays,
    myIncomingEtaDays: s.incomingEtaDays,
    mySharedSegments: s.sharedSegmentIds,
    myB2bSegmentId: s.b2bSegmentId,
  }
}

function hasIncomingData(incoming: DirectionalContextDto): boolean {
  return incoming.direct !== null || incoming.transit.length > 0
}

interface StoreCheckboxProps {
  store: StoreShortModel
  exclusions: number[]
  onChange: (code: number, excluded: boolean) => void
}

function StoreCheckbox({ store, exclusions, onChange }: StoreCheckboxProps) {
  return (
    <div className={cn.storeItem}>
      <Checkbox
        checked={!exclusions.includes(store.code)}
        onChange={(e) => onChange(store.code, !e.target.checked)}
      >
        <span className={cn.storeLabel}>
          <span>{store.description}</span>
          <span
            className={`${cn.storeStatusDot} ${store.isActive ? cn.activeStoreStatus : cn.inactiveStoreStatus}`}
            title={store.isActive ? 'Активен' : 'Неактивен'}
            aria-label={store.isActive ? 'Активен' : 'Неактивен'}
          />
          {store.isRemote && <Tag color="default">Удалённый</Tag>}
        </span>
      </Checkbox>
    </div>
  )
}

function PartnershipFormModal({
  open,
  mode,
  partnership,
  onClose,
  onSuccess,
  modal,
}: Props) {
  const [contextData, setContextData] =
    useState<PartnershipContextResponse | null>(null)
  const [contextLoading, setContextLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [b2bSegmentError, setB2bSegmentError] = useState(false)
  const [activeTab, setActiveTab] = useState('sell')
  const { selectedOrganisation } = useUserInfoStore((state) => state)
  const isOrgSelected = Object.keys(selectedOrganisation).length > 0
  const themeClass = isOrgSelected ? cn.blueTheme : cn.orangeTheme

  useEffect(() => {
    if (!open || !partnership) return

    setB2bSegmentError(false)
    setActiveTab('sell')
    setContextLoading(true)
    api
      .get<PartnershipContextResponse>('/partnerships/context/settings', {
        params: { targetSupplierId: partnership.partner.id },
      })
      .then((res) => {
        const context = sortPartnershipContext(res.data)
        setContextData(context)
        const initial =
          mode === 'settings' ? buildFormFromSettings(partnership) : EMPTY_FORM
        const segmentIds = new Set(context.myAvailableSegments.map((s) => s.id))
        setForm({
          ...initial,
          myB2bSegmentId:
            initial.myB2bSegmentId && segmentIds.has(initial.myB2bSegmentId)
              ? initial.myB2bSegmentId
              : null,
          mySharedSegments: initial.mySharedSegments.filter((id) =>
            segmentIds.has(id)
          ),
        })
      })
      .catch((e) => handleApiError(e, modal))
      .finally(() => setContextLoading(false))
  }, [open, partnership?.id, mode])

  const toggleExclusion = (
    field: 'localOutgoingExclusions' | 'localIncomingExclusions',
    code: number,
    excluded: boolean
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: excluded
        ? [...prev[field], code]
        : prev[field].filter((c) => c !== code),
    }))
  }

  const handleSubmit = async () => {
    if (!partnership) return

    const result = partnershipFormSchema.safeParse({
      myB2bSegmentId: form.myB2bSegmentId ?? undefined,
    })
    if (!result.success) {
      const errs = result.error.flatten().fieldErrors
      setB2bSegmentError(!!errs.myB2bSegmentId)
      toast.error(errs.myB2bSegmentId?.[0] ?? 'Заполните обязательные поля')
      return
    }
    setB2bSegmentError(false)

    setSubmitting(true)
    const label =
      mode === 'accept' ? 'Принятие партнёрства...' : 'Сохранение настроек...'
    const tId = toast.loading(label)

    try {
      const payload: AcceptPartnershipRequest = {
        enableMySharing: form.enableMySharing,
        myEtaDays: form.myEtaDays,
        myIncomingEtaDays: form.myIncomingEtaDays,
        myOutgoingExclusions: form.localOutgoingExclusions,
        myIncomingExclusions: form.localIncomingExclusions,
        mySharedSegments: form.mySharedSegments,
        myB2bSegmentId: form.myB2bSegmentId,
      }

      const response =
        mode === 'accept'
          ? await api.post<{ partnership: PartnershipModel }>(
              `/partnerships/${partnership.id}/actions/accept`,
              payload
            )
          : await api.put<{ partnership: PartnershipModel }>(
              `/partnerships/${partnership.id}/settings`,
              payload
            )

      toast.success(
        mode === 'accept' ? 'Партнёрство принято' : 'Настройки сохранены',
        { id: tId }
      )
      onSuccess(response.data.partnership)
    } catch (e) {
      toast.error('Ошибка', { id: tId })
      handleApiError(e, modal)
    } finally {
      setSubmitting(false)
    }
  }

  const title =
    mode === 'accept'
      ? `Принять партнёрство — ${partnership?.partner.description}`
      : `Настройки — ${partnership?.partner.description}`

  const outgoing = contextData?.outgoing
  const incoming = contextData?.incoming
  const showIncomingBlock = incoming && hasIncomingData(incoming)

  return (
    <Modal
      title={title}
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText={mode === 'accept' ? 'Принять' : 'Сохранить'}
      cancelText="Отмена"
      confirmLoading={submitting}
      centered
      width={580}
      destroyOnHidden
      wrapClassName={themeClass}
    >
      {contextLoading ? (
        <div className={cn.loaderWrap}>
          <Loader size={24} />
        </div>
      ) : (
        <div className={cn.form}>
          {mode === 'accept' && partnership && (
            <div className={cn.partnerBanner}>
              <div className={cn.bannerLabel}>Предложение от партнёра</div>
              <div className={cn.bannerRows}>
                <div className={cn.bannerRow}>
                  <span>Передаёт остатки:</span>
                  <strong>
                    {partnership.partnerSettings.sharesData ? 'Да' : 'Нет'}
                  </strong>
                </div>
                <div className={cn.bannerRow}>
                  <span>ETA до меня:</span>
                  <strong>
                    {partnership.partnerSettings.etaDays > 0
                      ? `${partnership.partnerSettings.etaDays} дн.`
                      : '—'}
                  </strong>
                </div>
              </div>
              <div className={cn.bannerHint}>
                Заполните свои встречные условия ниже
              </div>
            </div>
          )}

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              { key: 'sell', label: 'Продаю' },
              { key: 'buy', label: 'Покупаю' },
            ]}
          />

          {activeTab === 'sell' && (
            <div className={cn.roleSection}>
              <div className={cn.section}>
                <div className={cn.sectionTitle}>
                  Исходящая логистика (Что мы отдаём партнёру)
                </div>
                <div className={cn.switchRow}>
                  <Switch
                    checked={form.enableMySharing}
                    onChange={(v) =>
                      setForm((prev) => ({ ...prev, enableMySharing: v }))
                    }
                    size="small"
                  />
                  <span className={cn.switchLabel}>
                    Транслировать мои остатки партнёру
                  </span>
                </div>

                {form.enableMySharing && (
                  <>
                    <Checkbox
                      checked={
                        !form.showOutgoingList &&
                        form.localOutgoingExclusions.length === 0
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setForm((prev) => ({
                            ...prev,
                            showOutgoingList: false,
                            localOutgoingExclusions: [],
                          }))
                        } else {
                          setForm((prev) => ({
                            ...prev,
                            showOutgoingList: true,
                          }))
                        }
                      }}
                    >
                      Транслировать все доступные склады
                    </Checkbox>

                    {(form.showOutgoingList ||
                      form.localOutgoingExclusions.length > 0) &&
                      outgoing && (
                        <div className={cn.storeList}>
                          <div className={cn.storeHint}>
                            Снимите галочку — склад будет <strong>скрыт</strong>{' '}
                            от партнёра
                          </div>

                          {outgoing.direct && (
                            <div className={cn.supplierStoreGroup}>
                              <div className={cn.supplierGroupLabel}>
                                Собственные склады
                              </div>
                              <div className={cn.groupStores}>
                                {outgoing.direct.stores.map((store) => (
                                  <StoreCheckbox
                                    key={store.code}
                                    store={store}
                                    exclusions={form.localOutgoingExclusions}
                                    onChange={(code, excluded) =>
                                      toggleExclusion(
                                        'localOutgoingExclusions',
                                        code,
                                        excluded
                                      )
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {outgoing.transit.map((transitGroup, idx) => (
                            <div key={idx} className={cn.supplierStoreGroup}>
                              <div className={cn.supplierGroupLabel}>
                                {transitGroup.transitRoute
                                  .map((r) => r.description)
                                  .join(' → ')}
                              </div>
                              <div className={cn.groupStores}>
                                {transitGroup.stores.map((store) => (
                                  <StoreCheckbox
                                    key={store.code}
                                    store={store}
                                    exclusions={form.localOutgoingExclusions}
                                    onChange={(code, excluded) =>
                                      toggleExclusion(
                                        'localOutgoingExclusions',
                                        code,
                                        excluded
                                      )
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          ))}

                          {!outgoing.direct &&
                            outgoing.transit.length === 0 && (
                              <div className={cn.emptyHint}>
                                Нет доступных складов
                              </div>
                            )}
                        </div>
                      )}
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === 'buy' && showIncomingBlock && incoming && (
            <div className={cn.roleSection}>
              <div className={cn.section}>
                <div className={cn.sectionTitle}>
                  Входящая логистика (Что мы принимаем от партнёра)
                </div>
                <Checkbox
                  checked={
                    !form.showIncomingList &&
                    form.localIncomingExclusions.length === 0
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setForm((prev) => ({
                        ...prev,
                        showIncomingList: false,
                        localIncomingExclusions: [],
                      }))
                    } else {
                      setForm((prev) => ({ ...prev, showIncomingList: true }))
                    }
                  }}
                >
                  Принимать все транслируемые склады
                </Checkbox>

                {(form.showIncomingList ||
                  form.localIncomingExclusions.length > 0) && (
                  <div className={cn.storeList}>
                    <div className={cn.storeHint}>
                      Снимите галочку — остатки этого склада{' '}
                      <strong>не будут</strong> отображаться у вас
                    </div>

                    {incoming.direct && (
                      <div className={cn.supplierStoreGroup}>
                        <div className={cn.supplierGroupLabel}>
                          Собственные склады партнёра
                        </div>
                        <div className={cn.groupStores}>
                          {incoming.direct.stores.map((store) => (
                            <StoreCheckbox
                              key={store.code}
                              store={store}
                              exclusions={form.localIncomingExclusions}
                              onChange={(code, excluded) =>
                                toggleExclusion(
                                  'localIncomingExclusions',
                                  code,
                                  excluded
                                )
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {incoming.transit.map((transitGroup, idx) => (
                      <div key={idx} className={cn.supplierStoreGroup}>
                        <div className={cn.supplierGroupLabel}>
                          {transitGroup.transitRoute
                            .map((r) => r.description)
                            .join(' → ')}
                        </div>
                        <div className={cn.groupStores}>
                          {transitGroup.stores.map((store) => (
                            <StoreCheckbox
                              key={store.code}
                              store={store}
                              exclusions={form.localIncomingExclusions}
                              onChange={(code, excluded) =>
                                toggleExclusion(
                                  'localIncomingExclusions',
                                  code,
                                  excluded
                                )
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'buy' && !showIncomingBlock && (
            <div className={cn.roleSection}>
              <div className={cn.emptyHint}>
                Партнёр не транслирует доступные склады
              </div>
            </div>
          )}

          {activeTab === 'buy' && (
            <div className={cn.roleSection}>
              <div className={cn.field}>
                <label className={cn.label}>
                  Дополнительное время на приемку (дней)
                </label>
                <Input
                  type="number"
                  min={0}
                  value={form.myIncomingEtaDays}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      myIncomingEtaDays: Math.max(
                        0,
                        parseInt(e.target.value) || 0
                      ),
                    }))
                  }
                  className={cn.etaInput}
                />
              </div>
            </div>
          )}

          {activeTab === 'sell' && (
            <div className={cn.roleSection}>
              <div className={cn.sectionTitle}>Условия поставки партнёру</div>
              <div className={cn.field}>
                <label className={cn.label}>Доставка до партнёра (дней)</label>
                <Input
                  type="number"
                  min={0}
                  value={form.myEtaDays}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      myEtaDays: parseInt(e.target.value) || 0,
                    }))
                  }
                  className={cn.etaInput}
                />
              </div>

              <div className={cn.field}>
                <label className={cn.label}>
                  Сегменты для расширения (как База)
                </label>
                <div className={cn.fieldHint}>
                  Партнёр сможет создавать свои сегменты с наценкой на основе
                  этих
                </div>
                <Select
                  mode="multiple"
                  popupClassName={themeClass}
                  value={form.mySharedSegments}
                  onChange={(v) =>
                    setForm((prev) => ({ ...prev, mySharedSegments: v }))
                  }
                  placeholder="Выберите сегменты"
                  className={cn.fullWidth}
                  options={contextData?.myAvailableSegments.map((s) => ({
                    value: s.id,
                    label: s.description,
                  }))}
                />
              </div>

              <div className={cn.field}>
                <label className={cn.label}>
                  Сегмент для взаиморасчётов (B2B прайс){' '}
                  <span className={cn.required}>*</span>
                </label>
                <div className={cn.fieldHint}>
                  По этому сегменту партнёр будет покупать у вас напрямую
                </div>
                <Select
                  popupClassName={themeClass}
                  value={form.myB2bSegmentId || undefined}
                  onChange={(v) => {
                    setB2bSegmentError(false)
                    setForm((prev) => ({ ...prev, myB2bSegmentId: v ?? null }))
                  }}
                  placeholder="Выберите сегмент"
                  allowClear
                  className={cn.fullWidth}
                  status={b2bSegmentError ? 'error' : undefined}
                  options={contextData?.myAvailableSegments.map((s) => ({
                    value: s.id,
                    label: s.description,
                  }))}
                />
                {b2bSegmentError && (
                  <span className={cn.errorText}>Выберите B2B сегмент</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

export default PartnershipFormModal
